<script setup>
import { computed, ref, getCurrentInstance, watch } from 'vue'
import _ from 'lodash'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from "@kyvg/vue3-notification"
import utils from '@/utils'
import TabGroup from './Atoms/TabGroup.vue'
import TabItem from './Atoms/TabItem.vue'
import FormInput from './Atoms/FormInput.vue'
import FormCheckbox from './Atoms/FormCheckbox.vue'
import SrtPidMapping from './ui/SrtPidMapping.vue'
import { srtPassphraseError } from '@/Utils/srtPushTarget'

const instance = getCurrentInstance()
const root = instance.proxy.$root
const route = useRoute()
const router = useRouter()
const store = useStore()
const { notify } = useNotification()

//Emits
const emit = defineEmits({
	'platform-details': () => true
})

// Refs
const customPlatform = ref({
	mode: 'rtmp',
	isAuthRequired: false,
	server: null,
	key: null,
	username: null,
	password: null,
	port: null,
	latency: 120,
	passphrase: null,
	pids: '',
	pmtPid: '',
	pidsValid: true,
})
const errors = ref({
	server: false,
})

// Computed
const serverComputed = computed(() => {
	if (customPlatform.value.mode === 'rtmp') {
		return customPlatform.value.isAuthRequired && customPlatform.value.username && customPlatform.value.password
			? "rtmp://" + customPlatform.value?.username + ":" + customPlatform.value?.password + "@" + customPlatform.value?.server?.replace(/^(rtmp\:\/\/)/, "")
			: customPlatform.value.server
	} else if (customPlatform.value.server?.startsWith('srt://')) return customPlatform.value.server
	return 'srt://' + customPlatform.value.server
})
const passphraseError = computed(() => srtPassphraseError(customPlatform.value.passphrase))

// Watchers
watch(customPlatform, (newValue) => {
	errors.value.server = false
	const customPlatform = {}
	newValue.mode === 'rtmp'
		? (
			customPlatform.server = serverComputed.value,
			customPlatform.key = newValue.key)
		: (
			customPlatform.mode = 'srt',
			customPlatform.server = serverComputed.value,
			customPlatform.key = newValue.key,
			customPlatform.port = newValue.port,
			customPlatform.latency = newValue.latency,
			customPlatform.passphrase = newValue.passphrase,
			customPlatform.pids = newValue.pids,
			customPlatform.pmtPid = newValue.pmtPid,
			customPlatform.srtValid = newValue.pidsValid && !passphraseError.value
		)
	emit('platform-details', customPlatform)
}, {
	deep: true
})

// Methods
const clearForm = () => {
	customPlatform.value = {
		mode: customPlatform.value.mode,
		isAuthRequired: false,
		server: null,
		key: null,
		username: null,
		password: null,
		port: null,
		latency: 120,
		passphrase: null,
		pids: '',
		pmtPid: '',
		pidsValid: true,
	}
}

const validate = (type) => {
	if (type === 'server' && customPlatform.value.server) {
		const { valid } = utils.validateURL(customPlatform.value.server, { validatePathname: false })
		valid
			? errors.value[type] = false
			: errors.value[type] = true
	}
}

const setPlatformMode = (mode) => {
	clearForm()
	customPlatform.value.mode = mode
}
</script>

<template>
	<section class="relative  overflow-y-auto overflow-x-hidden max-h-[45vh] lg:max-h-[100vh]">
		<TabGroup class="w-full bg-surface-3 !rounded-none mt-10 mb-6">
			<TabItem class="w-1/2" type="tab" @click="setPlatformMode('rtmp')" :isActive="customPlatform.mode === 'rtmp'">RTMP
			</TabItem>
			<TabItem class="w-1/2" type="tab" @click="setPlatformMode('srt')" :isActive="customPlatform.mode === 'srt'">SRT
			</TabItem>
		</TabGroup>
		<template v-if="customPlatform.mode === 'rtmp'">
			<p v-if="errors.server" class="text-danger text-sm absolute top-2 error-message m-0">Invalid
				destination ingest address</p>
			<FormInput label="streaming server" v-model="customPlatform.server" placeholder="rtmp://broadcaster_addr/"
				:class="{ error: errors.server }" class="mt-6 !mb-4" @blur="validate('server')" />
			<FormCheckbox class="mb-3" v-model="customPlatform.isAuthRequired" label="Authentication Required" />
			<template v-if="customPlatform.isAuthRequired">
				<FormInput v-model="customPlatform.username" placeholder="Username" class="!mb-4" />
				<FormInput type="password" v-model="customPlatform.password" placeholder="Password" size="md" class="!mb-4" />
			</template>
			<FormInput label="streaming key" v-model="customPlatform.key" placeholder="#streaming_key" size="md"
				class="!mb-4" />
		</template>
		<template v-if="customPlatform.mode === 'srt'">
			<p v-if="customPlatform.port > 65535" class="text-danger text-sm absolute top-2 error-message">Invalid
				destination ingest address</p>
			<FormInput label="Host" v-model="customPlatform.server" placeholder="srt-ingest.example.com" class="!mb-4" />
			<FormInput label="Port" v-model="customPlatform.port" min="0" max="65535" placeholder="9998" type="number" class="!mb-4" />
			<FormInput label="Stream Id" v-model="customPlatform.key" placeholder="stream id" size="md" class="!mb-4" />
			<FormInput label="Latency" v-model="customPlatform.latency" type="number" placeholder="120" size="md" class="!mb-4" />
			<FormInput label="Passphrase" v-model="customPlatform.passphrase" placeholder="Passphrase" size="md" class="!mb-4"
				:is-error="!!passphraseError" :helper="passphraseError || undefined" />
			<SrtPidMapping protocol="srt" v-model:pids="customPlatform.pids" v-model:pmtPid="customPlatform.pmtPid"
				:host="customPlatform.server || ''" :port="customPlatform.port || ''" :stream-id="customPlatform.key || ''"
				:latency="customPlatform.latency || ''" :passphrase="customPlatform.passphrase || ''"
				@update:valid="customPlatform.pidsValid = $event" />
		</template>
	</section>
</template>